import { useState, useEffect } from 'react';
import {
  Clock, Loader2, AlertTriangle, Check, ChevronDown, X, Zap, Target,
  ArrowUpRight, ArrowDownRight, BarChart2, Activity, Eye, Layers,
  Timer, RefreshCw, Shield, Trash2, Info, Play
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useActivePolling } from '../hooks/useActivePolling';
import { InfoTip } from '../components/ui';
import TradingViewChart from '../components/TradingViewChart';
import {
  createTwapOrder, createVwapOrder, createIcebergOrder, createBracketOrder,
  getAlgoOrders, cancelAlgoOrder, getExchangeConnections,
  getExchangeMarkets, getExchangeTicker,
} from '../services/api';

const ALGO_TYPES = [
  { id: 'twap', name: 'TWAP', icon: Clock, color: 'from-blue-400 to-cyan-500', desc: 'Split large orders into equal slices over time — minimize market impact' },
  { id: 'vwap', name: 'VWAP', icon: BarChart2, color: 'from-violet-400 to-purple-500', desc: 'Match historical volume patterns — bigger slices when typical volume is higher' },
  { id: 'iceberg', name: 'Iceberg', icon: Eye, color: 'from-cyan-400 to-blue-500', desc: 'Hide true size — limit order shows only a small slice, refills as fills' },
  { id: 'bracket', name: 'Bracket (OCO)', icon: Target, color: 'from-amber-400 to-orange-500', desc: 'Entry + Take Profit + Stop Loss linked. When one closes, the other cancels' },
];

const FALLBACK_PAIRS = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT'];

// Sort: popular bases first, then by stablecoin quote priority, then alphabetic.
function sortPairs(pairs) {
  const POPULAR_BASES  = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'DOGE', 'ADA', 'AVAX', 'LINK', 'MATIC', 'DOT', 'UNI', 'ATOM', 'NEAR', 'APT', 'ARB', 'OP'];
  const POPULAR_QUOTES = ['USDT', 'USDC', 'USD', 'BUSD', 'DAI'];
  const rank = (p) => {
    const [base, quote] = p.split('/');
    const baseRank  = POPULAR_BASES.indexOf(base);
    const quoteRank = POPULAR_QUOTES.indexOf(quote);
    return (quoteRank === -1 ? 100 : quoteRank) * 1000 + (baseRank === -1 ? 999 : baseRank);
  };
  return [...pairs].sort((a, b) => {
    const ra = rank(a), rb = rank(b);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
}

export default function AlgoOrdersPage() {
  const { user } = useAuth();
  const [algoType, setAlgoType] = useState('twap');
  const [side, setSide] = useState('buy');
  const [symbol, setSymbol] = useState('BTC/USDT');
  const [exchange, setExchange] = useState('');
  const [exchanges, setExchanges] = useState([]);
  const [pairs, setPairs] = useState(FALLBACK_PAIRS);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [pairSearch, setPairSearch] = useState('');
  const [tickerPrice, setTickerPrice] = useState(null);
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState('active'); // 'active' | 'done' | 'all'
  const [placing, setPlacing] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  // TWAP/VWAP params
  const [totalAmount, setTotalAmount] = useState('');
  const [duration, setDuration] = useState('60');
  const [sliceCount, setSliceCount] = useState('10');

  // Iceberg params
  const [visibleAmount, setVisibleAmount] = useState('');
  const [icebergPrice, setIcebergPrice] = useState('');

  // Bracket params
  const [bracketAmount, setBracketAmount] = useState('');
  const [entryPrice, setEntryPrice] = useState('');
  // TP/SL have two input modes: percent (1, 2, 5%) or absolute price.
  // Percent mode is far less error-prone — users would otherwise need a
  // calculator to compute e.g. "2% above $2297.83". The computed absolute
  // prices (takeProfitPrice, stopLossPrice) are what we send to the backend
  // and what the API requires.
  const [tpMode, setTpMode] = useState('percent'); // 'percent' | 'price'
  const [slMode, setSlMode] = useState('percent');
  const [tpInput, setTpInput] = useState(''); // raw user input in whichever mode
  const [slInput, setSlInput] = useState('');
  const [takeProfitPrice, setTakeProfitPrice] = useState('');
  const [stopLossPrice, setStopLossPrice] = useState('');

  // Amount mode (BASE = ETH, QUOTE = USDC). Lets users think in either unit.
  // Same toggle pattern as CEX Trading. On submit we convert to base using the
  // live ticker before sending to the backend.
  const [amountMode, setAmountMode] = useState('base');

  // Chart interval — bound to the TradingView embed. 1h is a good default for
  // most algo decisions; users can switch between 15m / 1h / 4h / 1D inline.
  const [chartInterval, setChartInterval] = useState('60');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [conn, ord] = await Promise.all([getExchangeConnections(), getAlgoOrders()]);
      const connected = (conn.exchanges || []).filter(e => e.connected);
      setExchanges(connected);
      if (connected.length > 0 && !exchange) {
        setExchange(connected[0].exchangeId);
      }
      setOrders(ord.orders || []);
    } catch {} finally { setLoading(false); }
  }

  // Auto-refresh order list every 15s while tab is active and user not idle.
  // The backend worker ticks every 30s; polling at 15s means users see state
  // changes (Filled, Phase, TP/SL hits) within ~half a worker cycle without
  // needing to manually refresh. Polling pauses when the tab is hidden.
  const pollInterval = useActivePolling(15000, true);
  useEffect(() => {
    if (!pollInterval) return;
    const id = setInterval(async () => {
      try {
        const ord = await getAlgoOrders();
        setOrders(ord.orders || []);
      } catch {}
    }, pollInterval);
    return () => clearInterval(id);
  }, [pollInterval]);

  // Dynamic per-exchange pair list. Same pattern as CEX Trading + Smart Trade.
  useEffect(() => {
    if (!exchange) return;
    let cancelled = false;
    setPairsLoading(true);
    setPairSearch('');
    getExchangeMarkets(exchange)
      .then(res => {
        if (cancelled) return;
        const all = (res?.markets || []).map(m => m.symbol);
        if (all.length === 0) { setPairs(FALLBACK_PAIRS); return; }
        const sorted = sortPairs(all);
        setPairs(sorted);
        if (!sorted.includes(symbol)) setSymbol(sorted[0]);
      })
      .catch(() => { if (!cancelled) setPairs(FALLBACK_PAIRS); })
      .finally(() => { if (!cancelled) setPairsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchange]);

  // Live ticker — refresh every 15s while user is composing an order. Same
  // helper banner as CEX Trading so the user can convert their typed amount
  // to a USD value mentally.
  useEffect(() => {
    if (!exchange || !symbol) return;
    let cancelled = false;
    setTickerPrice(null);
    async function refresh() {
      try {
        const t = await getExchangeTicker(exchange, symbol);
        if (!cancelled && t?.last && t.last > 0) setTickerPrice(t.last);
      } catch { /* leave null */ }
    }
    refresh();
    const id = setInterval(refresh, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [exchange, symbol]);

  const filteredPairs = pairSearch
    ? pairs.filter(p => p.toLowerCase().includes(pairSearch.toLowerCase())).slice(0, 100)
    : pairs.slice(0, 100);

  const [base, quote] = (symbol || '/').split('/');

  // Recompute the absolute TP/SL prices whenever the input, mode, or entry
  // price changes. The backend requires absolute prices, but percent mode is
  // what most users intuit — we store both and submit absolute.
  // NOTE: these useEffects must come AFTER `quote` is destructured above —
  // referencing `quote` in the dependency array before its declaration would
  // trigger a temporal-dead-zone ReferenceError and blank the page.
  useEffect(() => {
    const e = parseFloat(entryPrice);
    const v = parseFloat(tpInput);
    if (!Number.isFinite(e) || !Number.isFinite(v) || e <= 0) {
      setTakeProfitPrice('');
      return;
    }
    if (tpMode === 'percent') {
      setTakeProfitPrice((e * (1 + v / 100)).toFixed(quote === 'USDT' || quote === 'USDC' || quote === 'USD' ? 2 : 6));
    } else {
      setTakeProfitPrice(String(v));
    }
  }, [tpInput, tpMode, entryPrice, quote]);

  useEffect(() => {
    const e = parseFloat(entryPrice);
    const v = parseFloat(slInput);
    if (!Number.isFinite(e) || !Number.isFinite(v) || e <= 0) {
      setStopLossPrice('');
      return;
    }
    if (slMode === 'percent') {
      // SL percent is always interpreted as "below entry" for buy, regardless of sign.
      setStopLossPrice((e * (1 - Math.abs(v) / 100)).toFixed(quote === 'USDT' || quote === 'USDC' || quote === 'USD' ? 2 : 6));
    } else {
      setStopLossPrice(String(v));
    }
  }, [slInput, slMode, entryPrice, quote]);

  function fmtQuote(n) {
    if (!Number.isFinite(n)) return '—';
    if (['USDT', 'USDC', 'USD', 'BUSD', 'DAI'].includes(quote)) return n.toFixed(2);
    if (n >= 100) return n.toFixed(2);
    if (n >= 1)   return n.toFixed(4);
    return n.toFixed(6);
  }
  function fmtBase(n) {
    if (!Number.isFinite(n)) return '—';
    if (n >= 1)    return n.toFixed(4);
    if (n >= 0.01) return n.toFixed(6);
    return n.toFixed(8);
  }
  // Returns the BASE-unit amount for an input that may be in base or quote.
  // Used both for the live-conversion line and for the submit payload.
  function toBase(input) {
    const n = parseFloat(input);
    if (!Number.isFinite(n) || n <= 0) return 0;
    if (amountMode === 'base') return n;
    if (!tickerPrice || tickerPrice <= 0) return 0;
    return n / tickerPrice;
  }
  function toQuote(input) {
    const n = parseFloat(input);
    if (!Number.isFinite(n) || n <= 0) return 0;
    if (amountMode === 'quote') return n;
    if (!tickerPrice || tickerPrice <= 0) return 0;
    return n * tickerPrice;
  }

  async function handleCreate() {
    setPlacing(true); setResult(null);
    try {
      // Quote-mode entries depend on a live ticker. If price is missing, fail
      // fast with a clear message rather than sending zero.
      if (amountMode === 'quote' && (!tickerPrice || tickerPrice <= 0)) {
        throw new Error('No live price for this pair — switch to base-amount mode or wait for the price banner to load.');
      }

      let res;
      if (algoType === 'twap' || algoType === 'vwap') {
        const baseAmount = toBase(totalAmount);
        if (baseAmount <= 0) throw new Error('Enter a total amount greater than zero.');
        res = algoType === 'twap'
          ? await createTwapOrder(exchange, symbol, side, baseAmount, parseInt(duration), parseInt(sliceCount))
          : await createVwapOrder(exchange, symbol, side, baseAmount, parseInt(duration), parseInt(sliceCount));
      } else if (algoType === 'iceberg') {
        const baseTotal   = toBase(totalAmount);
        const baseVisible = toBase(visibleAmount);
        if (baseTotal <= 0 || baseVisible <= 0) throw new Error('Enter both total and visible amounts greater than zero.');
        res = await createIcebergOrder(exchange, symbol, side, baseTotal, baseVisible, parseFloat(icebergPrice) || null);
      } else if (algoType === 'bracket') {
        const baseAmount = toBase(bracketAmount);
        if (baseAmount <= 0) throw new Error('Enter an amount greater than zero.');
        res = await createBracketOrder(exchange, symbol, side, baseAmount, parseFloat(entryPrice), parseFloat(takeProfitPrice), parseFloat(stopLossPrice));
      }
      setResult(res);
      const ord = await getAlgoOrders();
      setOrders(ord.orders || []);
    } catch (err) { setResult({ error: err.message }); }
    setPlacing(false);
  }

  async function handleCancel(orderId) {
    try { await cancelAlgoOrder(orderId); } catch (err) { alert(err.message); }
    const ord = await getAlgoOrders();
    setOrders(ord.orders || []);
  }

  const selectedAlgo = ALGO_TYPES.find(a => a.id === algoType);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;

  if (exchanges.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-cyan-400/10 flex items-center justify-center mx-auto mb-4"><Layers className="w-8 h-8 text-cyan-400" /></div>
          <h2 className="text-xl font-bold text-white mb-2">Algo Orders</h2>
          <p className="text-gray-400 mb-4">Connect an exchange in Settings → Connections to use TWAP, VWAP, Iceberg, and Bracket orders.</p>
          <a href="/settings?tab=connections" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500 text-dark-900 font-bold text-sm hover:bg-cyan-400 transition-all">Go to Settings → Connections</a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center"><Layers className="w-5 h-5 text-white" /></div>
          Algo Orders
          <span className="text-[9px] font-bold px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-400/15 to-blue-400/15 text-cyan-400 border border-cyan-400/25 tracking-wider">INSTITUTIONAL</span>
        </h1>
        <p className="text-gray-500 text-sm mt-1">TWAP, VWAP, Iceberg, and Bracket orders — execute like a hedge fund</p>
      </div>

      {/* Algo type selector */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {ALGO_TYPES.map(algo => (
          <button key={algo.id} onClick={() => setAlgoType(algo.id)}
            className={`rounded-xl p-4 border text-left transition-all ${algoType === algo.id ? `bg-gradient-to-br ${algo.color} bg-opacity-10 border-white/15` : 'bg-dark-800 border-dark-700 hover:border-dark-600'}`}>
            <algo.icon className={`w-5 h-5 mb-2 ${algoType === algo.id ? 'text-white' : 'text-gray-500'}`} />
            <p className={`text-sm font-bold ${algoType === algo.id ? 'text-white' : 'text-gray-400'}`}>{algo.name}</p>
            <p className={`text-[10px] mt-1 ${algoType === algo.id ? 'text-white/70' : 'text-gray-600'}`}>{algo.desc}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Order Form */}
        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <selectedAlgo.icon className="w-4 h-4 text-cyan-400" /> Create {selectedAlgo.name} Order
          </h3>

          {/* Exchange */}
          <div className="mb-3">
            <label className="text-gray-500 text-xs block mb-1">Exchange</label>
            <select value={exchange} onChange={e => setExchange(e.target.value)}
              className="w-full bg-cyan-400/10 border-2 border-cyan-400/40 rounded-xl px-4 py-2.5 text-cyan-400 text-sm font-bold focus:outline-none">
              {exchanges.map(ex => <option key={ex.exchangeId} value={ex.exchangeId} className="bg-dark-800 text-white">{ex.name}</option>)}
            </select>
          </div>

          {/* Side */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button onClick={() => setSide('buy')} className={`py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 ${side === 'buy' ? 'bg-emerald-500 text-dark-900' : 'bg-dark-700 text-gray-400'}`}><ArrowUpRight className="w-4 h-4" /> Buy</button>
            <button onClick={() => setSide('sell')} className={`py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 ${side === 'sell' ? 'bg-red-500 text-white' : 'bg-dark-700 text-gray-400'}`}><ArrowDownRight className="w-4 h-4" /> Sell</button>
          </div>

          {/* Symbol — searchable, dynamic per-exchange */}
          <div className="mb-3">
            <label className="text-gray-500 text-xs flex items-center justify-between mb-1">
              <span>Pair</span>
              <span className="text-gray-600 text-[10px]">
                {pairsLoading ? 'Loading…' : `${pairs.length.toLocaleString()} on this exchange`}
              </span>
            </label>
            <input
              type="text"
              value={pairSearch}
              onChange={e => setPairSearch(e.target.value)}
              placeholder="Search pair (e.g. BTC, ETH/USDC)"
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-xs font-mono placeholder-gray-600 focus:outline-none mb-2"
            />
            <select
              value={symbol}
              onChange={e => setSymbol(e.target.value)}
              size={Math.min(8, Math.max(3, filteredPairs.length))}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2 text-white text-sm font-mono focus:outline-none"
            >
              {filteredPairs.length === 0 ? (
                <option disabled>No pairs match "{pairSearch}"</option>
              ) : (
                filteredPairs.map(p => <option key={p} value={p}>{p}</option>)
              )}
            </select>
            {pairs.length > 100 && (
              <p className="text-gray-600 text-[10px] mt-1">Showing first 100 — refine search to find more</p>
            )}
          </div>

          {/* Live market price banner */}
          <div className="mb-3 rounded-xl bg-dark-700/40 border border-dark-600/40 px-3 py-2 flex items-center justify-between">
            <span className="text-gray-500 text-[11px]">Market price</span>
            {tickerPrice ? (
              <span className="text-white text-xs font-mono">1 {base} = <span className="text-cyan-400">{fmtQuote(tickerPrice)}</span> {quote}</span>
            ) : (
              <span className="text-gray-600 text-xs">Loading…</span>
            )}
          </div>

          {/* Amount-mode toggle — think in BASE or QUOTE currency.
              Same pattern as CEX Trading. Conversion happens at submit using
              the live ticker. */}
          <div className="mb-2 flex items-center justify-between">
            <label className="text-gray-500 text-xs flex items-center gap-1.5">
              Enter amount in
              <InfoTip side="right">QUOTE mode (USDT/USDC/USD) lets you type the dollar amount. BASE mode lets you type the crypto amount. For first algo orders, use QUOTE — slicing math is easier to reason about in dollars.</InfoTip>
            </label>
            <div className="inline-flex bg-dark-700 rounded-lg p-0.5 border border-dark-600">
              <button onClick={() => { setAmountMode('base'); setTotalAmount(''); setVisibleAmount(''); setBracketAmount(''); }}
                className={`px-2.5 py-1 rounded-md text-[10px] font-bold transition-all ${amountMode === 'base' ? 'bg-cyan-400 text-dark-900' : 'text-gray-500 hover:text-white'}`}>{base}</button>
              <button onClick={() => { setAmountMode('quote'); setTotalAmount(''); setVisibleAmount(''); setBracketAmount(''); }}
                className={`px-2.5 py-1 rounded-md text-[10px] font-bold transition-all ${amountMode === 'quote' ? 'bg-cyan-400 text-dark-900' : 'text-gray-500 hover:text-white'}`}>{quote}</button>
            </div>
          </div>

          {/* TWAP / VWAP params */}
          {(algoType === 'twap' || algoType === 'vwap') && (
            <>
              <div className="mb-3">
                <label className="text-gray-500 text-xs flex items-center justify-between mb-1">
                  <span>Total Amount ({amountMode === 'base' ? base : quote})</span>
                  {parseFloat(totalAmount) > 0 && tickerPrice && (
                    <span className="text-gray-600 text-[10px] font-mono">
                      ≈ {amountMode === 'base'
                          ? `${fmtQuote(toQuote(totalAmount))} ${quote}`
                          : `${fmtBase(toBase(totalAmount))} ${base}`}
                    </span>
                  )}
                </label>
                <input type="number" value={totalAmount} onChange={e => setTotalAmount(e.target.value)} step="any" placeholder="0.00"
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono placeholder-gray-600 focus:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <label className="text-gray-500 text-xs mb-1 flex items-center gap-1.5">
                    Duration (min)
                    <InfoTip side="top">How long the total execution spreads over. Longer = less per-slice impact + more directional drift risk. 60 min is a sensible starter. 4-24h windows absorb 5%+ market moves; size accordingly.</InfoTip>
                  </label>
                  <select value={duration} onChange={e => setDuration(e.target.value)}
                    className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none">
                    {[15, 30, 60, 120, 240, 480, 1440].map(m => <option key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60}h`}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-gray-500 text-xs mb-1 flex items-center gap-1.5">
                    Slices
                    <InfoTip side="top">How many child orders to break the total into. Rule of thumb: each slice should be &lt;0.5% of the pair's 24h volume. ±5% timing jitter is auto-added so slices don't follow a predictable cadence.</InfoTip>
                  </label>
                  <input type="number" value={sliceCount} onChange={e => setSliceCount(e.target.value)} min={2} max={100}
                    className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm font-mono focus:outline-none" />
                </div>
              </div>
              <div className="bg-dark-700/30 rounded-xl p-3 mb-3 space-y-1.5 border border-dark-600/30">
                <div className="flex justify-between text-xs"><span className="text-gray-500">Amount per slice</span><span className="text-white font-mono">{fmtBase(toBase(totalAmount) / Math.max(1, parseInt(sliceCount || 1)))} {base}{tickerPrice ? ` · ${fmtQuote(toQuote(totalAmount) / Math.max(1, parseInt(sliceCount || 1)))} ${quote}` : ''}</span></div>
                <div className="flex justify-between text-xs"><span className="text-gray-500">Interval</span><span className="text-white font-mono">{(parseInt(duration || 60) / parseInt(sliceCount || 1)).toFixed(1)} min</span></div>
                <div className="flex justify-between text-xs"><span className="text-gray-500">Strategy</span><span className="text-cyan-400">{algoType === 'twap' ? 'Equal time intervals' : 'Volume-weighted distribution'}</span></div>
              </div>
            </>
          )}

          {/* Iceberg params */}
          {algoType === 'iceberg' && (
            <>
              <div className="mb-3">
                <label className="text-gray-500 text-xs flex items-center justify-between mb-1">
                  <span>Total Amount, hidden ({amountMode === 'base' ? base : quote})</span>
                  {parseFloat(totalAmount) > 0 && tickerPrice && (
                    <span className="text-gray-600 text-[10px] font-mono">
                      ≈ {amountMode === 'base' ? `${fmtQuote(toQuote(totalAmount))} ${quote}` : `${fmtBase(toBase(totalAmount))} ${base}`}
                    </span>
                  )}
                </label>
                <input type="number" value={totalAmount} onChange={e => setTotalAmount(e.target.value)} step="any" placeholder="0.00"
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono placeholder-gray-600 focus:outline-none" />
              </div>
              <div className="mb-3">
                <label className="text-gray-500 text-xs flex items-center justify-between mb-1">
                  <span className="flex items-center gap-1.5">
                    Visible Amount, shown to market ({amountMode === 'base' ? base : quote})
                    <InfoTip side="right">How much of your total shows on the order book at any moment. Typical: 5-10% of total. So 0.1 BTC total / 0.01 BTC visible = only 10% of true size exposed. Refills automatically as the visible portion fills.</InfoTip>
                  </span>
                  {parseFloat(visibleAmount) > 0 && tickerPrice && (
                    <span className="text-gray-600 text-[10px] font-mono">
                      ≈ {amountMode === 'base' ? `${fmtQuote(toQuote(visibleAmount))} ${quote}` : `${fmtBase(toBase(visibleAmount))} ${base}`}
                    </span>
                  )}
                </label>
                <input type="number" value={visibleAmount} onChange={e => setVisibleAmount(e.target.value)} step="any" placeholder="0.00"
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono placeholder-gray-600 focus:outline-none" />
              </div>
              <div className="mb-3">
                <label className="text-gray-500 text-xs flex items-center justify-between mb-1">
                  <span>Limit Price <span className="text-gray-600">(optional — blank uses market)</span></span>
                  {tickerPrice && (
                    <button type="button" onClick={() => setIcebergPrice(String(tickerPrice))}
                      className="text-cyan-400 text-[10px] hover:text-cyan-300">
                      Use market ({fmtQuote(tickerPrice)})
                    </button>
                  )}
                </label>
                <input type="number" value={icebergPrice} onChange={e => setIcebergPrice(e.target.value)} step="any"
                  placeholder={tickerPrice ? String(tickerPrice) : 'Market price'}
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono placeholder-gray-600 focus:outline-none" />
              </div>
              <div className="bg-dark-700/30 rounded-xl p-3 mb-3 space-y-1.5 border border-dark-600/30">
                <div className="flex justify-between text-xs"><span className="text-gray-500">Hidden</span><span className="text-red-400 font-mono">{((1 - parseFloat(visibleAmount || 0) / parseFloat(totalAmount || 1)) * 100).toFixed(0)}% of order</span></div>
                <div className="flex justify-between text-xs"><span className="text-gray-500">Slices</span><span className="text-white font-mono">{Math.ceil(parseFloat(totalAmount || 1) / parseFloat(visibleAmount || 1))}</span></div>
                <div className="flex justify-between text-xs"><span className="text-gray-500">Strategy</span><span className="text-cyan-400">Auto-refill on each fill</span></div>
              </div>
            </>
          )}

          {/* Bracket params */}
          {algoType === 'bracket' && (
            <>
              <div className="mb-3">
                <label className="text-gray-500 text-xs flex items-center justify-between mb-1">
                  <span>Amount ({amountMode === 'base' ? base : quote})</span>
                  {parseFloat(bracketAmount) > 0 && tickerPrice && (
                    <span className="text-gray-600 text-[10px] font-mono">
                      ≈ {amountMode === 'base' ? `${fmtQuote(toQuote(bracketAmount))} ${quote}` : `${fmtBase(toBase(bracketAmount))} ${base}`}
                    </span>
                  )}
                </label>
                <input type="number" value={bracketAmount} onChange={e => setBracketAmount(e.target.value)} step="any" placeholder="0.00"
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono placeholder-gray-600 focus:outline-none" />
              </div>
              <div className="mb-3">
                <label className="text-gray-500 text-xs flex items-center justify-between mb-1">
                  <span className="flex items-center gap-1.5">
                    Entry Price <span className="text-gray-600">(limit)</span>
                    <InfoTip side="right">The limit price for your entry order. Set at or slightly above current market for near-instant fill. Set well below market to wait for a dip. Once entry fills, TP + SL orders are placed.</InfoTip>
                  </span>
                  {tickerPrice && (
                    <button type="button" onClick={() => setEntryPrice(String(tickerPrice))}
                      className="text-cyan-400 text-[10px] hover:text-cyan-300">
                      Use market ({fmtQuote(tickerPrice)})
                    </button>
                  )}
                </label>
                <input type="number" value={entryPrice} onChange={e => setEntryPrice(e.target.value)} step="any"
                  placeholder={tickerPrice ? String(tickerPrice) : '0.00'}
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono focus:outline-none" />
                <p className="text-gray-600 text-[10px] mt-1">
                  Bracket entry is a limit order. Set at or slightly above market for instant fill.
                </p>
              </div>

              {/* Take Profit */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-emerald-400 text-xs">Take Profit</label>
                  <div className="inline-flex bg-dark-900 rounded-md p-0.5 text-[10px] border border-dark-700">
                    <button type="button" onClick={() => setTpMode('percent')}
                      className={`px-2 py-0.5 rounded ${tpMode === 'percent' ? 'bg-emerald-500/20 text-emerald-300' : 'text-gray-500'}`}>%</button>
                    <button type="button" onClick={() => setTpMode('price')}
                      className={`px-2 py-0.5 rounded ${tpMode === 'price' ? 'bg-emerald-500/20 text-emerald-300' : 'text-gray-500'}`}>{quote || '$'}</button>
                  </div>
                </div>
                <div className="flex gap-1 mb-1.5 flex-wrap">
                  {tpMode === 'percent' ? (
                    ['1', '2', '5', '10'].map(p => (
                      <button key={p} type="button" onClick={() => setTpInput(p)}
                        className={`px-2 py-1 rounded-md text-[11px] border ${
                          tpInput === p ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-dark-900 border-dark-700 text-gray-400 hover:border-dark-600'
                        }`}>+{p}%</button>
                    ))
                  ) : (
                    entryPrice && [1, 2, 5, 10].map(p => {
                      const v = (parseFloat(entryPrice) * (1 + p / 100)).toFixed(quote === 'USDC' || quote === 'USDT' || quote === 'USD' ? 2 : 6);
                      return (
                        <button key={p} type="button" onClick={() => setTpInput(v)}
                          className={`px-2 py-1 rounded-md text-[11px] border ${
                            tpInput === v ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-dark-900 border-dark-700 text-gray-400 hover:border-dark-600'
                          }`}>+{p}%</button>
                      );
                    })
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input type="number" value={tpInput} onChange={e => setTpInput(e.target.value)} step="any"
                    placeholder={tpMode === 'percent' ? 'e.g. 2' : 'e.g. ' + (entryPrice ? (parseFloat(entryPrice) * 1.02).toFixed(2) : '0.00')}
                    className="flex-1 bg-dark-700 border border-emerald-500/30 rounded-xl px-3 py-2.5 text-white text-sm font-mono focus:outline-none" />
                  <span className="text-gray-500 text-xs font-mono w-16 text-right">{tpMode === 'percent' ? '%' : quote}</span>
                </div>
                {takeProfitPrice && entryPrice && (
                  <p className="text-gray-500 text-[10px] mt-1 font-mono">
                    {tpMode === 'percent'
                      ? `→ exit at ${takeProfitPrice} ${quote}`
                      : `→ ${((parseFloat(takeProfitPrice) / parseFloat(entryPrice) - 1) * 100).toFixed(2)}% above entry`}
                  </p>
                )}
              </div>

              {/* Stop Loss */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-red-400 text-xs">Stop Loss</label>
                  <div className="inline-flex bg-dark-900 rounded-md p-0.5 text-[10px] border border-dark-700">
                    <button type="button" onClick={() => setSlMode('percent')}
                      className={`px-2 py-0.5 rounded ${slMode === 'percent' ? 'bg-red-500/20 text-red-300' : 'text-gray-500'}`}>%</button>
                    <button type="button" onClick={() => setSlMode('price')}
                      className={`px-2 py-0.5 rounded ${slMode === 'price' ? 'bg-red-500/20 text-red-300' : 'text-gray-500'}`}>{quote || '$'}</button>
                  </div>
                </div>
                <div className="flex gap-1 mb-1.5 flex-wrap">
                  {slMode === 'percent' ? (
                    ['1', '2', '5', '10'].map(p => (
                      <button key={p} type="button" onClick={() => setSlInput(p)}
                        className={`px-2 py-1 rounded-md text-[11px] border ${
                          slInput === p ? 'bg-red-500/15 border-red-500/40 text-red-300' : 'bg-dark-900 border-dark-700 text-gray-400 hover:border-dark-600'
                        }`}>-{p}%</button>
                    ))
                  ) : (
                    entryPrice && [1, 2, 5, 10].map(p => {
                      const v = (parseFloat(entryPrice) * (1 - p / 100)).toFixed(quote === 'USDC' || quote === 'USDT' || quote === 'USD' ? 2 : 6);
                      return (
                        <button key={p} type="button" onClick={() => setSlInput(v)}
                          className={`px-2 py-1 rounded-md text-[11px] border ${
                            slInput === v ? 'bg-red-500/15 border-red-500/40 text-red-300' : 'bg-dark-900 border-dark-700 text-gray-400 hover:border-dark-600'
                          }`}>-{p}%</button>
                      );
                    })
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input type="number" value={slInput} onChange={e => setSlInput(e.target.value)} step="any"
                    placeholder={slMode === 'percent' ? 'e.g. 2' : 'e.g. ' + (entryPrice ? (parseFloat(entryPrice) * 0.98).toFixed(2) : '0.00')}
                    className="flex-1 bg-dark-700 border border-red-500/30 rounded-xl px-3 py-2.5 text-white text-sm font-mono focus:outline-none" />
                  <span className="text-gray-500 text-xs font-mono w-16 text-right">{slMode === 'percent' ? '%' : quote}</span>
                </div>
                {stopLossPrice && entryPrice && (
                  <p className="text-gray-500 text-[10px] mt-1 font-mono">
                    {slMode === 'percent'
                      ? `→ exit at ${stopLossPrice} ${quote}`
                      : `→ ${((parseFloat(stopLossPrice) / parseFloat(entryPrice) - 1) * 100).toFixed(2)}% below entry`}
                  </p>
                )}
              </div>

              {entryPrice && takeProfitPrice && stopLossPrice && (() => {
                // Correct dollar math: use BASE quantity, not the user-entered amount
                // (which could be in QUOTE mode). toBase() converts via the live ticker.
                const baseQty = parseFloat(toBase(bracketAmount));
                const e = parseFloat(entryPrice);
                const tp = parseFloat(takeProfitPrice);
                const sl = parseFloat(stopLossPrice);
                const risk = Number.isFinite(baseQty) ? Math.abs(e - sl) * baseQty : NaN;
                const reward = Number.isFinite(baseQty) ? Math.abs(tp - e) * baseQty : NaN;
                const rr = Math.abs(tp - e) / Math.abs(e - sl);
                return (
                  <div className="bg-dark-700/30 rounded-xl p-3 mb-3 space-y-1.5 border border-dark-600/30">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Risk if SL hits</span>
                      <span className="text-red-400 font-mono">{Number.isFinite(risk) ? `$${risk.toFixed(2)}` : '—'}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Reward if TP hits</span>
                      <span className="text-emerald-400 font-mono">{Number.isFinite(reward) ? `$${reward.toFixed(2)}` : '—'}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">R:R Ratio</span>
                      <span className="text-amber-400 font-bold">1:{Number.isFinite(rr) ? rr.toFixed(2) : '—'}</span>
                    </div>
                    <div className="flex justify-between text-xs"><span className="text-gray-500">OCO</span><span className="text-cyan-400">TP fills → SL cancels</span></div>
                  </div>
                );
              })()}
            </>
          )}

          {/* Result */}
          {result && (
            <div className={`rounded-xl p-3 mb-3 border ${result.error ? 'bg-red-500/5 border-red-500/20' : 'bg-emerald-500/5 border-emerald-500/20'}`}>
              {result.error ? <p className="text-xs text-red-400">{result.error}</p> : (
                <p className="text-xs text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> {selectedAlgo.name} order created — {
                  algoType === 'bracket' ? 'entry + TP + worker-monitored SL armed'
                  : algoType === 'iceberg' ? `${result.order?.sliceCount || Math.ceil(parseFloat(totalAmount || 1) / parseFloat(visibleAmount || 1))} slices queued (1 visible at a time)`
                  : `${result.order?.sliceCount || sliceCount || '?'} slices queued`
                }</p>
              )}
            </div>
          )}

          {/* Submit */}
          <button onClick={handleCreate} disabled={placing}
            className={`w-full py-3.5 rounded-xl text-sm font-bold bg-gradient-to-r ${selectedAlgo.color} text-white hover:opacity-90 disabled:opacity-40 transition-all flex items-center justify-center gap-2`}>
            {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Create {selectedAlgo.name} Order
          </button>
        </div>

        {/* Active Orders */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
            {(() => {
              const activeCount = orders.filter(o => o.status === 'active').length;
              const doneCount   = orders.length - activeCount;
              // Sort: active first, then by createdAt desc within each group
              const sorted = [...orders].sort((a, b) => {
                const aA = a.status === 'active' ? 0 : 1;
                const bA = b.status === 'active' ? 0 : 1;
                if (aA !== bA) return aA - bA;
                return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
              });
              const filtered = orderFilter === 'all'
                ? sorted
                : sorted.filter(o => orderFilter === 'active' ? o.status === 'active' : o.status !== 'active');

              return (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-bold flex items-center gap-2">
                      <Activity className="w-4 h-4 text-amber-400" /> Algo Orders
                    </h3>
                    <div className="flex items-center gap-2">
                      <div className="inline-flex bg-dark-700 rounded-lg p-0.5 border border-dark-600">
                        {[
                          { id: 'active', label: 'Active', count: activeCount, color: 'text-emerald-400' },
                          { id: 'done',   label: 'Done',   count: doneCount,   color: 'text-gray-400' },
                          { id: 'all',    label: 'All',    count: orders.length, color: 'text-cyan-400' },
                        ].map(f => (
                          <button key={f.id} onClick={() => setOrderFilter(f.id)}
                            className={`px-2.5 py-1 rounded-md text-[10px] font-bold flex items-center gap-1.5 transition-all ${
                              orderFilter === f.id ? `bg-dark-600 ${f.color}` : 'text-gray-500 hover:text-white'
                            }`}>
                            {f.label}
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${orderFilter === f.id ? 'bg-dark-700' : 'bg-dark-700/50'}`}>{f.count}</span>
                          </button>
                        ))}
                      </div>
                      <button onClick={loadData} className="p-2 rounded-lg text-gray-500 hover:text-white"><RefreshCw className="w-4 h-4" /></button>
                    </div>
                  </div>

                  {filtered.length === 0 ? (
                    <div className="text-center py-12">
                      <Layers className="w-10 h-10 text-gray-700 mx-auto mb-3" />
                      <p className="text-gray-500 text-sm">
                        {orderFilter === 'active' ? 'No active algo orders' : orderFilter === 'done' ? 'No completed or cancelled orders yet' : 'No algo orders yet'}
                      </p>
                      {orderFilter === 'active' && (
                        <p className="text-gray-600 text-xs mt-1">Create your first TWAP, VWAP, Iceberg, or Bracket order</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filtered.map(order => {
                        const algo = ALGO_TYPES.find(a => a.id === order.type) || ALGO_TYPES[0];
                        const isLong = order.side === 'buy';
                        const statusColor =
                          order.status === 'active' ? 'text-emerald-400' :
                          order.status === 'completed' ? 'text-cyan-400' :
                          order.status === 'partial' ? 'text-amber-400' :
                          order.status === 'cancelled' ? 'text-gray-500' :
                          order.status === 'failed' ? 'text-red-400' :
                          'text-amber-400';
                        return (
                          <div key={order.orderId} className={`rounded-xl border p-4 ${order.status === 'active' ? 'bg-dark-700/30 border-dark-600/50' : 'bg-dark-700/10 border-dark-600/20 opacity-70'}`}>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[9px] font-bold px-2 py-0.5 rounded bg-gradient-to-r ${algo.color} text-white`}>{algo.name}</span>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${isLong ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>{order.side?.toUpperCase()}</span>
                                <span className="text-white text-sm font-semibold">{order.symbol}</span>
                                <span className="text-gray-600 text-xs capitalize">on {order.exchangeId || order.exchange}</span>
                                {order.createdAt && <span className="text-gray-600 text-[10px]">· {new Date(order.createdAt).toLocaleString()}</span>}
                              </div>
                              {order.status === 'active' && (
                                <button onClick={() => handleCancel(order.orderId)} className="px-3 py-1.5 rounded-lg text-xs text-red-400 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20"><Trash2 className="w-3 h-3 inline mr-1" /> Cancel</button>
                              )}
                            </div>
                            {(() => {
                              // Bracket uses different fields than TWAP/VWAP/Iceberg.
                              // entryFilledAmount / entryFilledPrice get set by the worker
                              // when the entry leg fills; bracketStatus tracks phase.
                              const isBracket = order.type === 'bracket';
                              const filled = isBracket
                                ? Number(order.entryFilledAmount || 0)
                                : Number(order.filledAmount || 0);
                              const fillPrice = isBracket ? order.entryFilledPrice : order.avgPrice;
                              const bracketLabel = ({
                                awaiting_entry: 'AWAITING ENTRY',
                                entry_filled:   'ENTRY FILLED · MONITORING',
                                completed_tp:   'CLOSED · TP HIT',
                                completed_sl:   'CLOSED · SL HIT',
                              })[order.bracketStatus] || order.status?.toUpperCase();
                              const phaseLabel = isBracket ? bracketLabel : order.status?.toUpperCase();
                              return (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  <div><p className="text-gray-500 text-[10px]">Total Amount</p><p className="text-white text-xs font-mono">{Number(order.totalAmount || order.amount || 0).toFixed(6)}</p></div>
                                  <div><p className="text-gray-500 text-[10px]">{isBracket ? 'Legs' : 'Slices'}</p><p className="text-white text-xs font-mono">{isBracket ? 'entry + TP + SL' : (order.sliceCount || '—')}</p></div>
                                  <div><p className="text-gray-500 text-[10px]">Filled</p><p className="text-emerald-400 text-xs font-mono">{filled.toFixed(6)}{fillPrice ? ` @ ${Number(fillPrice).toFixed(2)}` : ''}</p></div>
                                  <div><p className="text-gray-500 text-[10px]">{isBracket ? 'Phase' : 'Status'}</p><p className={`${statusColor} text-xs font-bold uppercase`}>{phaseLabel}</p></div>
                                </div>
                              );
                            })()}
                            {order.type === 'bracket' && (
                              <div className="mt-2 flex gap-3 text-[10px] flex-wrap">
                                <span className="text-emerald-400">TP: ${order.takeProfitPrice}</span>
                                <span className="text-red-400">SL: ${order.stopLossPrice}</span>
                                {order.riskRewardRatio && <span className="text-amber-400">R:R 1:{order.riskRewardRatio}</span>}
                                {order.tpPlacementError && <span className="text-amber-400" title={order.tpPlacementError}>⚠ TP placement issue</span>}
                              </div>
                            )}
                            {/* Partial-fill warning: surfaces when some slices failed
                                so users don't mistake "completed" for "fully filled". */}
                            {order.status === 'partial' && (order.failedSliceCount > 0 || order.failureReason) && (
                              <div className="mt-2 bg-amber-500/5 border border-amber-500/20 rounded-lg p-2 text-[11px]">
                                <span className="text-amber-300 font-semibold">⚠ Partial fill: </span>
                                <span className="text-amber-200/80">
                                  {order.executedSliceCount || 0} of {order.sliceCount || (order.slices?.length || '?')} slices executed
                                  {order.failureReason ? ` — ${order.failureReason}` : ''}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Info cards */}
          <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Info className="w-4 h-4 text-cyan-400" /> Algo Order Types Explained</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {ALGO_TYPES.map(algo => (
                <div key={algo.id} className="flex items-start gap-3 bg-dark-700/30 rounded-xl p-3">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${algo.color} flex items-center justify-center flex-shrink-0`}><algo.icon className="w-4 h-4 text-white" /></div>
                  <div><p className="text-white text-xs font-bold">{algo.name}</p><p className="text-gray-500 text-[10px] mt-0.5">{algo.desc}</p></div>
                </div>
              ))}
            </div>
          </div>

          {/* Live chart — shows the pair the user is composing an order for,
              on their selected exchange. Quick visual context for entry/TP/SL
              decisions. TradingView embed (free widget, no API key). */}
          {symbol && exchange && (
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-white font-bold flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-400" />
                  <span className="font-mono">{symbol}</span>
                  <span className="text-gray-500 text-xs font-normal capitalize">on {exchange}</span>
                </h3>
                <div className="inline-flex bg-dark-900 rounded-lg p-0.5 border border-dark-700 text-[11px]">
                  {[
                    { id: '15', label: '15m' },
                    { id: '60', label: '1h' },
                    { id: '240', label: '4h' },
                    { id: 'D',  label: '1D' },
                  ].map(iv => (
                    <button
                      key={iv.id}
                      onClick={() => setChartInterval(iv.id)}
                      className={`px-2.5 py-1 rounded-md transition-colors ${
                        chartInterval === iv.id ? 'bg-emerald-500/20 text-emerald-300' : 'text-gray-500 hover:text-gray-300'
                      }`}
                    >
                      {iv.label}
                    </button>
                  ))}
                </div>
              </div>
              <TradingViewChart
                symbol={symbol}
                exchangeId={exchange}
                interval={chartInterval}
                height={420}
              />
              <p className="text-gray-600 text-[10px] mt-2">
                Chart data via TradingView · informational only · not investment advice
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
